/**
 * Per-organization feature flags.
 *
 * Backed by `user.organization.features` (jsonb hash on the Rails side).
 * The hash form lets a feature carry config:
 *   { live_shopping: true } OR { live_shopping: { enabled: true, ... } }
 *
 * Audience-specific modules:
 *   - live_shopping     → TikTok/FB live sellers
 *   - kitchen_workflow  → restaurants
 *   - walk_in_pos       → boutiques
 *   - loyalty_program   → store credit / customer cards
 */
import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'

export type FeatureKey =
  | 'live_shopping'
  | 'kitchen_workflow'
  | 'walk_in_pos'
  | 'loyalty_program'

export function useFeatureFlags() {
  const authStore = useAuthStore()

  const features = computed<Record<string, unknown>>(() => {
    return (authStore.user as any)?.organization?.features ?? {}
  })

  const hasFeature = (key: FeatureKey | string): boolean => {
    const val = features.value[key]
    if (val == null) return false
    if (typeof val === 'object') {
      return !!(val as { enabled?: boolean }).enabled
    }
    return !!val
  }

  const liveShoppingEnabled = computed(() => hasFeature('live_shopping'))
  const kitchenWorkflowEnabled = computed(() => hasFeature('kitchen_workflow'))
  const walkInPosEnabled = computed(() => hasFeature('walk_in_pos'))
  const loyaltyProgramEnabled = computed(() => hasFeature('loyalty_program'))

  return {
    features,
    hasFeature,
    liveShoppingEnabled,
    kitchenWorkflowEnabled,
    walkInPosEnabled,
    loyaltyProgramEnabled
  }
}
